import React, { useCallback, useMemo, useRef } from 'react';
import type { Alert, Finding, FindingInput, UpdateFindingInput } from '#/graphql/types';
import Form, { FormAction, FormRefType } from '#/common/components/Form';
import { useNavigate } from 'react-router-dom';
import useToast from '#/common/hooks/useToast';
import useUpdateFindingMutation from '../../hooks/useUpdateFindingMutation';
import { get, has, isNil, omitBy } from 'lodash';
import useCreateFindingMutation from '../../hooks/useCreateFindingMutation';
import useDeleteFindingMutation from '../../hooks/useDeleteFindingMutation';
import { FindingStatus, FindingType } from '#/graphql/types';
import allFields from '../../utils/fields';
import AttachmentsUpload from '#/common/components/AttachmentUpload';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import { createFields, updateFields } from './fields';
import useSession from '#/common/hooks/useSession';
import { generateDescription, getHighestImpactPriority } from '#/modules/alert/utils/constants';
import { InitializedEventInfo } from 'devextreme/events';
import dxForm, { FieldDataChangedEvent } from 'devextreme/ui/form';
import { statusItems } from '../../utils/constants';
import { getItemOptions, setEditorOptions } from '#/common/components/Form/formItemOption';

export interface FindingFormProps {
  finding: Finding;
  title: string;
  alert: Alert | null | undefined;
}

const FindingForm: React.FC<FindingFormProps> = ({ finding, title, alert }) => {
  const { alert: showAlert, confirm } = useToast();
  const navigate = useNavigate();
  const formRef = useRef<FormRefType | null>(null);
  const id = get(finding, 'id', undefined);
  const tenantId = get(finding, 'tenantId', undefined);
  const assetId = get(finding, 'assetId', undefined);
  const status = get(finding, 'status', FindingStatus.new);

  const { user } = useSession();

  // Defined in the modules hooks dir
  const { updateFinding } = useUpdateFindingMutation();
  const { createFinding } = useCreateFindingMutation();
  const { deleteFinding } = useDeleteFindingMutation();

  // Determine if form should be editable based on finding status
  const isFormEditable = useMemo(() => {
    // Only allow editing for "new", "planned" and "in_progress" statuses
    return (
      !id ||
      status === FindingStatus.new ||
      status === FindingStatus.planned ||
      status === FindingStatus.in_progress
    );
  }, [id, status]);

  // Prepare the finding data with defaults for new records
  const findingWithDefaults = useMemo(() => {
    // If this is a new record (no ID), set default values only if they don't exist
    if (!id) {
      if (alert) {
        finding = {
          ...finding,
          assetId: alert.assetId ?? '',
          asset: alert.asset,
          tenantId: alert.tenantId,
          shortDescription: alert.exceptionName ?? '',
          type: FindingType.issue,
          reportedById: user?.id ?? '',
          description: generateDescription(alert),
          alertId: alert.id,
          priority: getHighestImpactPriority(
            alert.impactComfort,
            alert.impactEnergy,
            alert.impactReliability,
            'finding',
          ),
        } as Finding & Pick<FindingInput, 'alertId'>;
      }
      return {
        ...finding,
        status: status, // Only default to "new" if no status exists
        reportedOn: finding.reportedOn || new Date(), // Default to current date for new records
        // reportedById: finding.reportedById || user?.id, // Default to current user for new records
      };
    }
    // For existing records, use the data as-is
    return finding;
  }, [finding, id, status, alert]);

  const sourceFields = id ? updateFields : createFields;

  // Submit that updates or creates a new record based on if there is ID present
  const submit = useCallback(
    (data: FindingInput | UpdateFindingInput) => {
      // Filter out Finding-specific read-only fields
      const {
        id,
        number,
        createdAt,
        createdBy,
        updatedAt,
        updatedBy,
        asset,
        displayNumber,
        model,
        __typename,
        ...filteredData
      } = data as any;

      if (!id) {
        return createFinding(omitBy(filteredData, isNil) as FindingInput);
      }
      return updateFinding(id, omitBy(filteredData, isNil) as UpdateFindingInput);
    },
    [id, updateFinding, createFinding, isFormEditable],
  );

  const actions = useMemo(() => {
    const result: FormAction<Finding>[] = [];

    // Show delete button for existing findings, but disable if status is not "new"
    if (id && !finding?.isArchived) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        disabled: status !== FindingStatus.new,
        onClick: async (e) => {
          // Check if finding can be deleted (only "new" status)
          if (status !== FindingStatus.new) {
            await showAlert('warning', 'Only findings with "New" status can be deleted.');
            return;
          }

          const sure = await confirm('error', 'Are you sure you want to delete this finding?');
          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          await deleteFinding(id);
          e.setIsLoading(false);

          navigate('/findings');
        },
      });
    }

    if (id && !finding?.isArchived) {
      result.push({
        label: 'Create WO',
        type: 'normal',
        disabled: !isFormEditable,
        onClick: async (e) => {
          navigate(`/work-orders/new?findingId=${id}&assetId=${assetId}&tenantId=${tenantId}`);
        },
      });
    }

    // Add submit action (always show, but disable when not editable or no changes)
    if (submit) {
      result.push({
        label: finding?.isArchived ? 'Archived' : 'Submit',
        type: 'submit',
        disabled: !isFormEditable && finding?.status !== FindingStatus.in_review,
        onClick: async (e) => {
          if (!e.record) {
            return;
          }

          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void showAlert('error', `${title} is not valid, check field entries.`);
            return;
          }

          try {
            e.setIsLoading(true);
            const result = (await submit(e.record)) as any;
            e.setIsLoading(false);

            // For create operations, navigate to the new finding
            if (result?.errors) {
              void showAlert('error', result.errors.join(`;\n`));
              return;
            }
            if (!id && result?.data && result?.data.id) {
              navigate(`/findings/${result?.data.id}/details`, { replace: true });
            }

            void showAlert('success', 'Changes saved!');
            formRef.current?.repaint();
          } catch (error: any) {
            e.setIsLoading(false);
            // console.log(error);
            // Handle GraphQL errors that contain validation messages
            if (error.errors && error.errors.length > 0 && typeof error.errors === 'string') {
              const errorMessages = error.errors.map((err: any) => err.message).join('\n');
              void showAlert('error', errorMessages);
            } else if (error.message) {
              void showAlert('error', error.message);
            } else if (error.errors) {
              void showAlert('error', error.errors);
            } else {
              void showAlert('error', 'An error occurred while saving');
            }
          }
        },
      });
    }

    return result;
  }, [id, assetId, status, navigate, submit, isFormEditable, confirm, showAlert]);

  const onContentReady = useCallback((e: InitializedEventInfo<dxForm>) => {
    const { component: form } = e;

    if (!form) return;

    const formData = form.option('formData');

    if (formData?.status === FindingStatus.in_review) {
      const statusOptions = {
        readOnly: false,
        items: statusItems.map((item) =>
          item.key === 'resolved' ? { ...item, disabled: false } : { ...item, disabled: true },
        ),
      };
      setEditorOptions(form, 'status', statusOptions);
    }

    if (
      has(formData, 'thirdPartyId') &&
      has(formData, 'thirdPartySource') &&
      !!(formData.thirdPartyId && formData.thirdPartySource)
    ) {
      setEditorOptions(form, 'description', { readOnly: true });
      setEditorOptions(form, 'assetId', { readOnly: true });
    }

    setEditorOptions(form, 'reportedById', {
      dataStoreOptions: {
        ...getItemOptions(form, 'reportedById')?.editorOptions?.dataStoreOptions,
        accessTo: formData?.assetId ? { resource: 'assets', ids: [formData.assetId] } : undefined,
      },
    });
  }, []);

  const onFieldDataChanged = useCallback((e: FieldDataChangedEvent) => {
    const { component: form, dataField, value } = e;

    if (dataField === 'assetId') {
      form.updateData({ reportedById: null });
      setEditorOptions(form, 'reportedById', {
        dataStoreOptions: {
          ...getItemOptions(form, 'reportedById')?.editorOptions?.dataStoreOptions,
          accessTo: value ? { resource: 'assets', ids: [value] } : undefined,
        },
      });
    }
  }, []);

  return (
    <>
      <Form<Finding>
        ref={formRef}
        key={`finding-form-${id || 'new'}`}
        record={findingWithDefaults as Finding}
        fields={sourceFields}
        actions={actions}
        supportTenant
        readonly={!isFormEditable}
        onContentReady={onContentReady}
        onFieldDataChanged={onFieldDataChanged}
      />
      {id && (
        <AttachmentsUpload
          relatedTable="findings"
          disableUpload={!isFormEditable}
          relatedItem={id}
        />
      )}
    </>
  );
};

export default React.memo(FindingForm);
